import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Users, ChevronRight, ArrowRight, Mail, Ruler } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import FullWidthHero from '../components/FullWidthHero';
import LocalServiceZone from '../components/LocalServiceZone';
import { formatPrice } from '../utils/formatPrice';
import { realPhotos, realPhotosAlt } from '../utils/realPhotos';

const API = process.env.REACT_APP_BACKEND_URL;

// Category configurations - Using real client photos
const categoryConfig = {
  'spas-2-3-places': {
    title: 'Spas 2 à 3 places',
    subtitle: 'Compacts et intimistes',
    description: 'Découvrez notre sélection de spas 2 à 3 places, parfaits pour les couples ou les petits espaces. Profitez d\'un moment de détente à deux.',
    metaTitle: 'Spas 2 à 3 places | Atlantique Spa Boutique',
    metaDescription: 'Spas compacts 2 à 3 places. Idéal pour les couples et petits espaces. Livraison et installation en Loire-Atlantique, Vendée, Morbihan.',
    filter: (spa) => spa.places >= 2 && spa.places <= 3 && spa.category === 'spa-massage',
    heroImage: realPhotos.spa2_3PlacesHero
  },
  'spas-4-5-places': {
    title: 'Spas 4 à 5 places',
    subtitle: 'L\'équilibre parfait',
    description: 'Nos spas 4 à 5 places offrent le compromis idéal entre convivialité et espace. Parfaits pour les familles ou recevoir des amis.',
    metaTitle: 'Spas 4 à 5 places | Atlantique Spa Boutique',
    metaDescription: 'Spas familiaux 4 à 5 places. Équilibre parfait entre convivialité et confort. Installation en Grand Ouest.',
    filter: (spa) => spa.places >= 4 && spa.places <= 5 && spa.category === 'spa-massage',
    heroImage: realPhotos.installation1
  },
  'spas-6-places-et-plus': {
    title: 'Spas 6 places et plus',
    subtitle: 'Grand format pour grands moments',
    description: 'Nos spas 6 places et plus sont conçus pour les familles nombreuses et les moments conviviaux. Profitez ensemble d\'une expérience spa premium.',
    metaTitle: 'Spas 6 places et plus | Atlantique Spa Boutique',
    metaDescription: 'Grands spas 6 places et plus. Pour les familles et moments conviviaux. Vente et installation Loire-Atlantique, Vendée.',
    filter: (spa) => spa.places >= 6 && spa.category === 'spa-massage',
    heroImage: realPhotos.heroMain
  },
  'spas-debordement': {
    title: 'Spas à débordement',
    subtitle: 'L\'excellence du design',
    description: 'Nos spas à débordement allient esthétique haut de gamme et performance. Un choix d\'exception pour sublimer votre espace extérieur.',
    metaTitle: 'Spas à débordement | Atlantique Spa Boutique',
    metaDescription: 'Spas à débordement premium. Design luxueux et finitions haut de gamme. Installation sur-mesure en Grand Ouest.',
    filter: (spa) => spa.category === 'spa-debordement',
    heroImage: realPhotos.expertise
  },
  'spas-de-nage': {
    title: 'Spas de nage',
    subtitle: 'Sport et détente réunis',
    description: 'Nos spas de nage combinent les bienfaits de la natation à contre-courant et du spa. L\'équipement idéal pour les sportifs et amateurs de bien-être.',
    metaTitle: 'Spas de nage | Atlantique Spa Boutique',
    metaDescription: 'Spas de nage et couloirs de nage. Natation à contre-courant + balnéothérapie. Installation sur-mesure en Grand Ouest.',
    filter: (spa) => spa.category === 'spa-nage' || spa.gamme === 'SMAX' || spa.gamme === 'EMAX',
    heroImage: realPhotos.spaNageHero
  },
  'ice-bath': {
    title: 'ICE BATH',
    subtitle: 'Bains froids & récupération',
    description: 'Découvrez notre gamme ICE BATH dédiée aux bains froids et à la récupération. Idéal pour les sportifs et les adeptes du bien-être par le froid.',
    metaTitle: 'ICE BATH - Bains froids | Atlantique Spa Boutique',
    metaDescription: 'Bains froids ICE BATH pour récupération sportive. Cryothérapie et bien-être par le froid. Disponible en Grand Ouest.',
    filter: (spa) => spa.gamme === 'Bains Froids' || spa.category === 'bain-froid',
    heroImage: realPhotos.installation1
  }
};

const SpaCategoryPage = () => {
  const location = useLocation();
  const [spas, setSpas] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get category from URL path (e.g., /spas-2-3-places -> spas-2-3-places)
  const category = location.pathname.replace('/', '');
  const config = categoryConfig[category] || categoryConfig['spas-2-3-places'];

  useEffect(() => {
    const fetchSpas = async () => {
      try {
        const response = await axios.get(`${API}/api/spas`);
        const filteredSpas = response.data.filter(config.filter);
        setSpas(filteredSpas);
      } catch (error) {
        console.error('Error fetching spas:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSpas();
  }, [category, config.filter]);

  const breadcrumbs = [
    { name: 'Accueil', href: '/' },
    { name: 'Nos Spas', href: '/spas' },
    { name: config.title, href: `/${category}` }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1E3A5F]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`spa-category-${category}`}>
      <SEO
        title={config.metaTitle}
        description={config.metaDescription}
        canonical={`/${category}`}
      />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex items-center gap-2 text-sm">
            {breadcrumbs.map((crumb, index) => (
              <span key={crumb.href} className="flex items-center gap-2">
                {index > 0 && <ChevronRight className="w-4 h-4 text-slate-400" />}
                {index === breadcrumbs.length - 1 ? (
                  <span className="text-slate-600">{crumb.name}</span>
                ) : (
                  <Link to={crumb.href} className="text-[#1E3A5F] hover:underline">
                    {crumb.name}
                  </Link>
                )}
              </span>
            ))}
          </nav>
        </div>
      </div>

      {/* Hero Section - Full Width Image */}
      <FullWidthHero
        variant="compact"
        image={config.heroImage}
        imageAlt={config.title}
        badge={config.subtitle}
        title={config.title}
        subtitle={config.description}
        overlayPosition="left"
      />

      {/* Local Service Zone - ONLY on spa pages */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <LocalServiceZone />
      </div>

      {/* Results count */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <p className="text-slate-600">
          <span className="font-semibold text-[#1E3A5F]">{spas.length}</span> {spas.length > 1 ? 'spas trouvés' : 'spa trouvé'}
        </p>
      </div>

      {/* Spas Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {spas.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-700 mb-2">Aucun spa disponible</h3>
            <p className="text-slate-500 mb-6">Cette catégorie sera bientôt disponible.</p>
            <Link to="/spas">
              <Button className="bg-[#1E3A5F] hover:bg-[#0F172A]">
                Voir tous les spas
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {spas.map((spa) => (
              <Link
                key={spa.slug}
                to={`/spas/${spa.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100"
                data-testid={`spa-card-${spa.slug}`}
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden bg-white">
                  {spa.images && spa.images.length > 0 ? (
                    <img
                      src={spa.images[0]}
                      alt={spa.name}
                      className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-slate-100">
                      <img
                        src="https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=600&q=80"
                        alt={spa.name}
                        className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  {/* Badges */}
                  <div className="absolute top-4 left-4 flex flex-col gap-2">
                    {spa.gamme && (
                      <span className="px-3 py-1 bg-[#1E3A5F] text-white text-xs font-medium rounded-full">
                        {spa.gamme}
                      </span>
                    )}
                    {spa.in_stock && (
                      <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full">
                        En stock
                      </span>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                    {spa.brand || 'Origin Spa'}
                  </p>
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-3 group-hover:text-[#1E3A5F] transition-colors">
                    {spa.name}
                  </h3>

                  {/* Specs */}
                  <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                    <span className="flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-[#1E3A5F]" />
                      {spa.places} places
                    </span>
                    {spa.dimensions && (
                      <span className="flex items-center gap-1.5">
                        <Ruler className="w-4 h-4 text-[#1E3A5F]" />
                        {spa.dimensions}
                      </span>
                    )}
                  </div>

                  {/* Price */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    {spa.price && !spa.price_on_request ? (
                      <span className="text-xl font-bold text-[#F97316]">
                        {formatPrice(spa.price)}
                      </span>
                    ) : (
                      <span className="text-[#1E3A5F] font-medium">Sur devis</span>
                    )}
                    <span className="flex items-center gap-1 text-sm text-[#1E3A5F] font-medium group-hover:gap-2 transition-all">
                      Voir détails
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <section className="bg-[#0F172A] py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Besoin d'aide pour choisir ?
          </h2>
          <p className="text-white/70 mb-8">
            Notre expert vous accompagne dans le choix du spa adapté à vos besoins et à votre espace.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="mailto:contact@atlantiquespaboutique.com">
              <Button size="lg" className="bg-[#F97316] hover:bg-[#EA580C] gap-2">
                <Mail className="w-5 h-5" />
                Nous contacter
              </Button>
            </a>
            <Link to="/devis">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Demander un devis gratuit
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SpaCategoryPage;
